/**
 * AppHeader Component
 * Global application header with three-column layout:
 * - Left: App name and logo
 * - Center: Top-level navigation menu
 * - Right: User profile button
 */

import React, { useMemo } from 'react';
import AppLogo from './AppLogo';
import NavigationMenu from './NavigationMenu';
import ProfileButton from './ProfileButton';
import { AppHeaderProps } from './types';
import styles from './AppHeader.module.css';

/**
 * Main header component
 * Combines three subcomponents in a responsive three-column layout
 */
export const AppHeader: React.FC<AppHeaderProps> = ({
  appName,
  navigationItems,
  logoUrl,
  currentPath,
  onMenuItemSelected,
  className,
  userProfile,
}) => {
  // Validate and sort navigation items
  const validatedItems = useMemo(() => {
    if (!Array.isArray(navigationItems)) return [];
    return navigationItems.filter(
      (item) =>
        item &&
        item.id &&
        item.label &&
        item.path &&
        typeof item.order === 'number',
    );
  }, [navigationItems]);

  return (
    <header
      className={`${styles.header} ${className || ''}`}
      data-testid="app-header"
      role="banner"
    >
      <AppLogo appName={appName} logoUrl={logoUrl} />
      <NavigationMenu
        items={validatedItems}
        currentPath={currentPath}
        onMenuItemSelected={onMenuItemSelected}
      />
      <ProfileButton userProfile={userProfile} />
    </header>
  );
};

export default AppHeader;
